import React, { useEffect, useState } from "react";
import {
  MDBRow,
  MDBCol,
  MDBInput,
  MDBBtn,
  MDBIcon,
  MDBPopover,
  MDBPopoverBody,
  MDBPopoverHeader, 
  MDBSpinner,
  MDBRadio
} from "mdb-react-ui-kit";
import ExportModal from "./Modals/ExportModal";
import ExportQuickLinks from "./ExportQuickLinks";
import BillSearchDropdown from './BillSearchDropdown';

function HeaderSearch({
  searchTerm,
  onSearchChange,
  setModeOpen,
  modeOpen,
  filterSession,
  filterLegislature,
  filteredCommittees,
  selectedCommitteeTypes,
  setSelectedCommitteeTypes,
  relevantBills,
  selectedLegislators,
  selectedChambers,
  selectedBills,
  selectedBillTypes,
  selectedBillStatus,
  dateRange,
  loadingExtraData
}) {
  // Change default to Standing for Odd Years Jan-May 7, otherwise Interim
  const getDefaultCommitteeType = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth(); // 0-indexed: 0 = Jan
    const date = today.getDate();

    const isOddYear = year % 2 !== 0;
    const isInRange = (month === 0) || (month === 1) || (month === 2) || (month === 3) || (month === 4 && date <= 7);

    return isOddYear && isInRange ? "Standing" : "Interim";
  };

  const standingTypes = ["ST", "JS", "AS", "SS", "CC", "FC", "CW", "SE"]; // Default types for Jan–May odd years
  const interimTypes = ["IC", "AC", "OC", "JI", "SI"]; // Default types for other times

  const changeCommitteeTypes = (type) => {
    setSelectedCommitteeType(type)
    if(type === "All")
      setSelectedCommitteeTypes([...interimTypes, ...standingTypes])
    else if(type === "Interim")
      setSelectedCommitteeTypes(interimTypes)
    else if(type === "Standing")
      setSelectedCommitteeTypes(standingTypes)
  }

  const [basicModal, setBasicModal] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const toggleOpen = () => setBasicModal(!basicModal);
  const [selectedCommitteeType, setSelectedCommitteeType] = useState(getDefaultCommitteeType());
  const [defaultTypeInitialized, setDefaultTypeInitialized] = useState(false);

  useEffect(() => {
    if (
        filterLegislature &&
        filterLegislature?.startDate &&
        filterSession &&
        filterSession?.type
    ) {
      setDataReady(true);
      changeCommitteeTypes(selectedCommitteeType);
    } else {
      setDataReady(false);
    }
  }, [filterLegislature, filterSession]);

  useEffect(() => {
    if (!defaultTypeInitialized && ["All", "Interim", "Standing"].includes(selectedCommitteeType)) {
      changeCommitteeTypes(selectedCommitteeType);
      setDefaultTypeInitialized(true)
    }
    else if(selectedCommitteeTypes === "RESET")
      changeCommitteeTypes(selectedCommitteeType)
  }, [selectedCommitteeType, selectedCommitteeTypes]);

  return (
    <>
      <MDBRow className="g-1">
        <MDBCol size="lg" className="text-center text-md-start mb-3 mb-md-0">
          {dataReady && (
            <h6 className="lead mb-0">
              {new Date(filterLegislature.startDate).getFullYear().toString()} (
              {filterSession.type}) Session &{" "}
              {filterLegislature?.ordinals || ""}th Legislature
            </h6>
          )}
          <h3 className="title">Committee Explorer</h3>
        </MDBCol>
        <MDBCol
          size="lg" lg="4"
          className="d-flex justify-content-end align-items-center mt-0 mt-lg-4"
        >
          <MDBInput
            label="Search Committee Names or (S) for Senate, (H) for House"
            id="search"
            className="form-control bg-white"
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </MDBCol>
        <MDBCol size="auto">
          <div className="d-flex justify-content-end helpers">
            <MDBPopover
              tag="a"
              href="#"
              btnChildren={<MDBIcon fas icon="info-circle" />}
              dismiss
              placement="top"
            >
              <MDBPopoverHeader>Quick Search</MDBPopoverHeader>
              <MDBPopoverBody>
                Using the search bar will search committee titles, acronyms &
                types for the currently selected session. Use the Filters for
                more in-depth searching.
              </MDBPopoverBody>
            </MDBPopover>
            <a
              href="https://www.legmt.gov/committees/"
              target="_blank"
              className="ms-1"
            >
              <MDBIcon fas icon="question-circle" />
            </a>
          </div>
          <MDBBtn color="primary" onClick={() => setModeOpen(!modeOpen)}>
            <MDBIcon fas icon="filter" /> Filters / Reporting
          </MDBBtn>
          {loadingExtraData ? (
              <>
                <span className="mx-3">
                  <MDBSpinner size="sm" role="status" tag="span" className="me-2" />
                   </span>
              </>
          ) : (
              <>
                <MDBBtn onClick={toggleOpen} color="primary" className="ms-1">
                  <MDBIcon fas icon="cloud-download-alt" /> Export
                </MDBBtn>
              </>
          )}
        </MDBCol>
      </MDBRow>
      <MDBRow>
        <MDBCol lg="10">
          <div className="d-flex justify-content-start badge-filter">
            <MDBRadio
              name="committeeTypes"
              value="All"
              label={<span className="badge">All</span>}
              inline
              checked={selectedCommitteeType === "All"}
              onChange={() => changeCommitteeTypes("All")}
            />
            <MDBRadio
              name="committeeTypes"
              value="Interim"
              label={<span className="badge">Interim</span>}
              inline
              checked={selectedCommitteeType === "Interim"}
              onChange={() => changeCommitteeTypes("Interim")}
            />
            <MDBRadio
              name="committeeTypes"
              value="Standing"
              label={<span className="badge">Standing</span>}
              inline
              checked={selectedCommitteeType === "Standing"}
              onChange={() => changeCommitteeTypes("Standing")}
            />
          </div>
          {selectedCommitteeType !== "Interim" && (
            <ExportQuickLinks
              filteredCommittees={filteredCommittees}
              loadingExtraData={loadingExtraData}
            />
          )}
        </MDBCol>
        <MDBCol lg="2">
          <BillSearchDropdown filterSession={filterSession} relevantBills={relevantBills}/>
        </MDBCol>
      </MDBRow>
      <ExportModal
        toggleOpen={toggleOpen}
        basicModal={basicModal}
        filteredCommittees={filteredCommittees}
        selectedCommitteeTypes={selectedCommitteeTypes}
        setBasicModal={setBasicModal}
        selectedLegislators={selectedLegislators}
        selectedChambers={selectedChambers}
        selectedBills={selectedBills}
        selectedBillTypes={selectedBillTypes}
        selectedBillStatus={selectedBillStatus}
        dateRange={dateRange}
      />
    </>
  );
}

export default HeaderSearch;
